import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MoonIcon, PrinterIcon, LockKeyIcon, CheckCircleIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Manual Day End dialog. Shows the operational snapshot for a chosen date
 * (arrivals, departures, in-house, no-shows, revenue, collections, expenses)
 * and lets a manager formally close the day. Closing is idempotent per date.
 */
export default function DayEndDialog({ open, onClose, prefill }) {
  const { activeId, active } = useProperty();
  const [date, setDate] = useState(today());
  const [summary, setSummary] = useState(null);
  const [notes, setNotes] = useState("");
  const [markNoShows, setMarkNoShows] = useState(true);
  const [platform, setPlatform] = useState({});
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/day-end/summary", { params: { property_id: activeId, date } }),
        api.get(`/settings/property/${activeId}`).catch(() => ({ data: { effective: {} } })),
      ]);
      setSummary(s.data);
      setPlatform(p?.data?.effective || {});
      setNotes(s.data?.notes || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load day-end");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    // If a caller pre-populated a specific date (e.g. from the overdue banner),
    // start on that date; otherwise default to today.
    setDate(prefill?.date || today());
  }, [open, prefill]);

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, activeId, date]);

  const closeDay = async () => {
    if (!summary) return;
    if (summary.closed) return toast.info("Day already closed");
    try {
      await api.post("/day-end/close", {
        property_id: activeId,
        date,
        notes,
        mark_no_shows: markNoShows,
      });
      toast.success("Day closed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    }
  };

  const printSummary = () => {
    if (!summary) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return toast.error("Popup blocked — allow popups then try again");
    w.document.write(buildDayEndHtml({ summary, active, platform }));
    w.document.close();
  };

  const c = summary?.counts || {};
  const r = summary?.revenue || {};
  const col = summary?.collections || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl" data-testid="dayend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MoonIcon size={18} weight="duotone" className="text-primary" /> Day End · {active?.name || "Property"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
              <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="w-44 mt-1" data-testid="dayend-date" />
            </div>
            {summary?.closed && (
              <div className="ml-auto flex items-center gap-2 rounded-md bg-primary/10 text-primary px-3 py-2 text-xs" data-testid="dayend-closed-badge">
                <CheckCircleIcon size={14} weight="fill" />
                <span>Closed by {summary.closed_by} · {summary.closed_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
            )}
          </div>

          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

          {summary && !loading && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dayend-counts">
                <StatCard label="Arrivals" value={c.arrivals} />
                <StatCard label="Departures" value={c.departures} />
                <StatCard label="In-house" value={c.in_house} />
                <StatCard label="No-shows" value={c.no_shows} highlight={c.no_shows > 0} />
                <StatCard label="Cancellations" value={c.cancellations} />
                <StatCard label="Occupied rooms" value={`${c.occupied}/${c.total_rooms}`} />
                <StatCard label="Occupancy" value={`${c.occupancy_pct}%`} />
                <StatCard label="Invoices" value={summary.invoice_count} />
              </div>

              <div>
                <p className="label-overline mb-2">Revenue</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="dayend-revenue">
                  <MoneyCard label="Rooms" value={r.rooms} />
                  <MoneyCard label="POS (direct)" value={r.pos_direct} />
                  <MoneyCard label="POS on folio" value={r.pos_on_invoice} />
                  <MoneyCard label="Discounts" value={r.discount} negative />
                  <MoneyCard label="Tax collected" value={r.tax} />
                  <MoneyCard label="Expenses" value={r.expenses} negative />
                  <MoneyCard label="Paid" value={r.paid_amount} accent />
                  <MoneyCard label="Unpaid" value={r.unpaid_amount} negative />
                </div>
              </div>

              <div>
                <p className="label-overline mb-2">Collections by mode</p>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="dayend-collections">
                  <MoneyCard label="Cash" value={col.cash} />
                  <MoneyCard label="UPI" value={col.upi} />
                  <MoneyCard label="Card" value={col.card} />
                  <MoneyCard label="Bank" value={col.bank} />
                  <MoneyCard label="Travels" value={col.travels} />
                  <MoneyCard label="Other" value={col.other} />
                </div>
              </div>

              {!summary.closed && (
                <div className="rounded-md border border-border p-3 space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes (optional)</Label>
                    <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Handover note, cash short/over, incidents…" className="mt-1" data-testid="dayend-notes" />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={markNoShows} onCheckedChange={(v) => setMarkNoShows(!!v)} data-testid="dayend-no-shows" />
                    <span>Auto-mark <b>{c.no_shows || 0}</b> unarrived booking(s) as <span className="uppercase tracking-wider">No-show</span></span>
                  </label>
                </div>
              )}

              {summary.notes && summary.closed && (
                <div className="rounded-md bg-muted/40 p-3 text-xs" data-testid="dayend-view-notes">
                  <p className="label-overline">Closed with note</p>
                  <p className="mt-1 whitespace-pre-wrap">{summary.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          <Button type="button" variant="outline" onClick={printSummary} disabled={!summary} data-testid="dayend-print-btn">
            <PrinterIcon size={14} className="mr-1" /> Print
          </Button>
          <Button type="button" onClick={closeDay} disabled={!summary || summary.closed} data-testid="dayend-close-btn">
            <LockKeyIcon size={14} className="mr-1" /> {summary?.closed ? "Already closed" : "Close day"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-md border p-3 ${highlight ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" : "border-border"}`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-display text-2xl mt-1">{value ?? 0}</p>
    </div>
  );
}

function MoneyCard({ label, value, negative, accent }) {
  const cls = accent ? "text-primary" : negative ? "text-[hsl(var(--warning))]" : "";
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`font-mono-plex text-sm mt-1 ${cls}`}>{inr(value)}</p>
    </div>
  );
}

function _esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
}

function buildDayEndHtml({ summary, active, platform }) {
  const propName = active?.name || platform.group_name || "Hotel";
  const propAddress = active?.address || platform.address || "";
  const propCity = [active?.city, active?.state].filter(Boolean).join(", ");
  const gst = platform.gst_number || "";
  const logo = platform.logo_url || null;
  const c = summary.counts || {};
  const r = summary.revenue || {};
  const col = summary.collections || {};
  const row = (l, v) => `<tr><td>${_esc(l)}</td><td class="r">${_esc(v)}</td></tr>`;
  const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  return `<!doctype html><html><head><title>Day End · ${_esc(propName)} · ${_esc(summary.date)}</title><meta charset="utf-8" />
<style>
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; }
  body { font-family: 'Inter', system-ui, sans-serif; color:#16241B; margin:24px; font-size:12px; }
  .header { display:flex; align-items:center; gap:16px; border-bottom:3px double #2F5D50; padding-bottom:12px; margin-bottom:12px; }
  .header img { height:56px; width:auto; }
  h1 { font-family:'Playfair Display', serif; color:#2F5D50; margin:0; font-size:22px; }
  .meta { font-size:10px; color:#4E534D; margin-top:4px; }
  h2 { font-family:'Playfair Display', serif; color:#2F5D50; font-size:14px; text-transform:uppercase; letter-spacing:0.08em; border-bottom:1px solid #C8A24B; padding-bottom:3px; margin:16px 0 6px; }
  table { width:100%; border-collapse:collapse; font-size:11px; }
  td { padding:4px 6px; border-bottom:1px solid #E5E5DE; }
  td.r { text-align:right; font-family: 'JetBrains Mono', monospace; }
  .grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
  .close-banner { margin-top:16px; padding:8px 12px; background:#F5F1E6; border-left:4px solid #C8A24B; font-size:11px; }
  .footer { margin-top:24px; text-align:right; font-size:9px; color:#4E534D; text-transform:uppercase; letter-spacing:0.05em; }
  @page { margin:12mm; }
</style></head><body>
<div class="header">
  ${logo ? `<img src="${_esc(logo)}" alt="" />` : ""}
  <div>
    <h1>${_esc(propName)}</h1>
    <div class="meta">${_esc(propAddress)}<br/>${_esc(propCity)}${gst ? " · GSTIN: " + _esc(gst) : ""}</div>
  </div>
</div>
<h2 style="text-align:center; border:none;">Day End Report · ${_esc(summary.date)}</h2>
<div class="grid">
  <div>
    <h2>Operations</h2>
    <table>
      ${row("Arrivals", c.arrivals ?? 0)}
      ${row("Departures", c.departures ?? 0)}
      ${row("In-house", c.in_house ?? 0)}
      ${row("No-shows", c.no_shows ?? 0)}
      ${row("Cancellations", c.cancellations ?? 0)}
      ${row("Occupied / total rooms", `${c.occupied ?? 0} / ${c.total_rooms ?? 0}`)}
      ${row("Occupancy", (c.occupancy_pct ?? 0) + "%")}
      ${row("Invoices raised", summary.invoice_count ?? 0)}
    </table>
  </div>
  <div>
    <h2>Revenue</h2>
    <table>
      ${row("Rooms", money(r.rooms))}
      ${row("POS (direct)", money(r.pos_direct))}
      ${row("POS on folio", money(r.pos_on_invoice))}
      ${row("Discounts", "− " + money(r.discount))}
      ${row("Tax collected", money(r.tax))}
      ${row("Expenses", "− " + money(r.expenses))}
      ${row("Paid", money(r.paid_amount))}
      ${row("Unpaid", money(r.unpaid_amount))}
    </table>
  </div>
</div>
<h2>Collections by mode</h2>
<table>
  ${row("Cash", money(col.cash))}
  ${row("UPI", money(col.upi))}
  ${row("Card", money(col.card))}
  ${row("Bank", money(col.bank))}
  ${row("Travels", money(col.travels))}
  ${row("Other", money(col.other))}
</table>
${summary.closed ? `<div class="close-banner"><b>Closed</b> by ${_esc(summary.closed_by || "")} at ${_esc((summary.closed_at || "").slice(0, 16).replace("T", " "))}${summary.notes ? "<br/>Note: " + _esc(summary.notes) : ""}</div>` : `<div class="close-banner">This day has not been formally closed yet.</div>`}
<div class="footer">Printed ${_esc(new Date().toLocaleString("en-IN"))}</div>
<script>window.addEventListener('load', () => setTimeout(() => { window.focus(); window.print(); }, 300));</script>
</body></html>`;
}
